import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  addMonths,
  format,
  getDay,
  getDaysInMonth,
  isSameDay,
  setMonth,
  setYear,
  startOfMonth,
  subMonths,
} from "date-fns";

interface Props {
  value: Date | null;
  onChange: (value: Date) => void;
  onBlur?: () => void;
  placeholder?: string;
  min?: Date;
  max?: Date;
}

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTHS = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

export const CustomDatePicker = forwardRef<HTMLDivElement, Props>(
  ({ value, onChange, onBlur, placeholder = "Выберите дату", min, max }, _ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState<Date>(value ?? new Date(2015, 0, 1));
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          if (isOpen) {
            setIsOpen(false);
            onBlur?.();
          }
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [isOpen, onBlur]);

    const grid = useMemo(() => {
      const monthStart = startOfMonth(viewDate);
      const offset = (getDay(monthStart) + 6) % 7;
      const daysInMonth = getDaysInMonth(viewDate);
      const cells: Array<{ date: Date | null }> = [];
      for (let i = 0; i < offset; i++) cells.push({ date: null });
      for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ date: new Date(viewDate.getFullYear(), viewDate.getMonth(), d) });
      }
      while (cells.length % 7 !== 0) cells.push({ date: null });
      return cells;
    }, [viewDate]);

    const years = useMemo(() => {
      const current = new Date().getFullYear();
      return Array.from({ length: 60 }, (_, i) => current - i);
    }, []);

    const today = new Date();

    const isDisabled = (date: Date) => {
      if (min && date < new Date(min.getFullYear(), min.getMonth(), min.getDate())) return true;
      if (max && date > new Date(max.getFullYear(), max.getMonth(), max.getDate())) return true;
      return false;
    };

    return (
      <Wrapper ref={ref}>
        <Trigger type="button" onClick={() => setIsOpen((p) => !p)} $open={isOpen} $hasValue={!!value}>
          <CalendarIcon viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="5" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.7" fill="none" />
            <path d="M3 9H17" stroke="currentColor" strokeWidth="1.7" />
            <path d="M7 3V6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <path d="M13 3V6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </CalendarIcon>
          <span>{value ? format(value, "dd.MM.yyyy") : placeholder}</span>
        </Trigger>
        {isOpen && (
          <Popup>
            <Header>
              <NavBtn type="button" onClick={() => setViewDate(subMonths(viewDate, 1))}>
                <svg width="10" height="14" viewBox="0 0 10 14" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 1L2 7L8 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </NavBtn>
              <Selects>
                <HeaderSelect
                  value={viewDate.getMonth()}
                  onChange={(e) => setViewDate(setMonth(viewDate, Number(e.target.value)))}
                >
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i}>{m}</option>
                  ))}
                </HeaderSelect>
                <HeaderSelect
                  value={viewDate.getFullYear()}
                  onChange={(e) => setViewDate(setYear(viewDate, Number(e.target.value)))}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </HeaderSelect>
              </Selects>
              <NavBtn type="button" onClick={() => setViewDate(addMonths(viewDate, 1))}>
                <svg width="10" height="14" viewBox="0 0 10 14" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 1L8 7L2 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </NavBtn>
            </Header>
            <WeekdaysRow>
              {WEEKDAYS.map((d) => (
                <Weekday key={d}>{d}</Weekday>
              ))}
            </WeekdaysRow>
            <Grid>
              {grid.map((cell, idx) => {
                if (!cell.date) return <DayCell key={idx} $empty />;
                const selected = value ? isSameDay(cell.date, value) : false;
                const isToday = isSameDay(cell.date, today);
                const disabled = isDisabled(cell.date);
                return (
                  <DayCell
                    key={idx}
                    $selected={selected}
                    $today={isToday}
                    $disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      onChange(cell.date as Date);
                      setIsOpen(false);
                      onBlur?.();
                    }}
                  >
                    {cell.date.getDate()}
                  </DayCell>
                );
              })}
            </Grid>
          </Popup>
        )}
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 8px;
`;

const Trigger = styled.button<{ $open: boolean; $hasValue: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: white;
  border: 1.5px solid ${({ $open }) => ($open ? "var(--color-primary)" : "var(--color-border)")};
  border-radius: var(--radius-md);
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  color: ${({ $hasValue }) => ($hasValue ? "var(--color-text)" : "#9aa491")};
  cursor: pointer;
  outline: none;
  transition: var(--transition);
  text-align: left;
  box-shadow: ${({ $open }) => ($open ? "0 0 0 4px var(--color-primary-soft)" : "none")};

  &:hover {
    border-color: ${({ $open }) => ($open ? "var(--color-primary)" : "var(--color-primary-light)")};
  }
`;

const CalendarIcon = styled.svg`
  width: 18px;
  height: 18px;
  color: var(--color-primary);
  flex-shrink: 0;
`;

const Popup = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  z-index: 20;
  padding: 16px;
  width: 320px;
  animation: slideDown 160ms ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 400px) {
    width: 280px;
    padding: 14px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-soft);
  border: none;
  cursor: pointer;
  color: var(--color-primary);
  transition: var(--transition);
  flex-shrink: 0;

  &:hover {
    background: var(--color-primary);
    color: white;
  }
`;

const Selects = styled.div`
  display: flex;
  gap: 6px;
  flex: 1;
  justify-content: center;
`;

const HeaderSelect = styled.select`
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  padding: 6px 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text);
  cursor: pointer;
  outline: none;
  transition: var(--transition);
  appearance: none;
  -webkit-appearance: none;
  text-align: center;
  text-align-last: center;

  &:hover,
  &:focus {
    background: var(--color-primary-soft);
    border-color: var(--color-primary-soft);
  }
`;

const WeekdaysRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 6px;
`;

const Weekday = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

interface DayCellProps {
  $selected?: boolean;
  $today?: boolean;
  $empty?: boolean;
  $disabled?: boolean;
}

const DayCell = styled.div<DayCellProps>`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  border-radius: var(--radius-sm);
  cursor: ${({ $empty, $disabled }) => ($empty || $disabled ? "default" : "pointer")};
  pointer-events: ${({ $empty }) => ($empty ? "none" : "auto")};
  opacity: ${({ $disabled }) => ($disabled ? 0.3 : 1)};
  transition: var(--transition);

  ${({ $selected }) =>
    $selected &&
    `
    background: var(--color-primary);
    color: white;
    font-weight: 700;
  `}

  ${({ $today, $selected }) =>
    $today && !$selected &&
    `
    color: var(--color-primary);
    font-weight: 700;
    border: 1.5px solid var(--color-primary);
  `}

  &:hover {
    ${({ $empty, $selected, $disabled }) =>
      !$empty && !$selected && !$disabled &&
      `
      background: var(--color-primary-soft);
      color: var(--color-primary);
    `}
  }
`;
